/**
 * RosterWithTeamSelection
 *
 * Wrapper component that checks for a selected team and either:
 * 1. Shows the roster for the selected team (NatStat data)
 * 2. Redirects to team selector if no team selected
 * 3. Falls back to showing the regular roster list
 */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ChevronRight, Search, Loader2, Zap, Activity } from 'lucide-react';
import { useSelectedTeamRoster } from '../hooks/useSelectedTeamRoster';
import { generateBiometricProfile } from '../utils/biometricDataGenerator';

/**
 * Team header showing the selected team info
 */
function TeamHeader({ teamName, leagueName, sport, onChangeTeam }) {
  return (
    <div className="flex items-center justify-between p-4 mb-6 rounded-[14px] border bg-[rgba(255,255,255,0.03)] border-[#1A1A1A]">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-[14px] flex items-center justify-center text-2xl font-bold bg-[#E8B800]/20 text-[#E8B800]">
          {teamName?.charAt(0) || '?'}
        </div>
        <div>
          <h2 className="text-xl font-bold text-[#F5F5F5]">
            {teamName}
          </h2>
          <p className="text-sm text-[#999999]">
            {leagueName} • {sport?.charAt(0).toUpperCase() + sport?.slice(1)}
          </p>
        </div>
      </div>
      <button
        onClick={onChangeTeam}
        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-[#E8B800] hover:bg-[#E8B800]/10"
      >
        Change Team
      </button>
    </div>
  );
}

/**
 * Player card for displaying roster member with biometric wearable data
 */
function PlayerCard({ player, onClick }) {
  // Generate biometric profile for this player
  const playerId = player.player_id || player.id;
  const biometrics = useMemo(
    () => generateBiometricProfile(playerId, player.player_name),
    [playerId, player.player_name]
  );

  return (
    <button
      onClick={onClick}
      className="group w-full text-left p-4 rounded-[14px] border transition-all bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)] hover:bg-[rgba(255,255,255,0.05)] hover:border-[#E8B800]/30"
    >
      <div className="flex items-center gap-4">
        {/* Player avatar with biometric indicator */}
        <div className="relative">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)]">
            {player.jersey_number || player.player_name?.charAt(0) || '?'}
          </div>
          {/* Biometric status dot */}
          {biometrics.hasAnyDevice && (
            <div
              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black ${
                biometrics.compositeReadiness >= 85 ? 'bg-emerald-500' :
                biometrics.compositeReadiness >= 70 ? 'bg-green-500' :
                biometrics.compositeReadiness >= 55 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              title={`Readiness: ${biometrics.compositeReadiness}%`}
            />
          )}
        </div>

        {/* Player info */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate text-[#F5F5F5]">
            {player.player_name}
          </div>
          <div className="text-sm text-[#999999]">
            {player.position && <span className="mr-2">{player.position}</span>}
            {player.jersey_number && <span>#{player.jersey_number}</span>}
          </div>

          {/* Biometric wearables row */}
          {biometrics.hasAnyDevice && (
            <div className="flex items-center gap-2 mt-1.5">
              {/* Device indicators */}
              <div className="flex items-center gap-1">
                {Object.entries(biometrics.devices)
                  .filter(([_, data]) => data.connected)
                  .slice(0, 3)
                  .map(([device, data]) => (
                    <div
                      key={device}
                      className="w-4 h-4 rounded flex items-center justify-center"
                      style={{ backgroundColor: `${data.color}20` }}
                      title={data.deviceName}
                    >
                      {device === 'whoop' && <Zap size={8} style={{ color: data.color }} />}
                      {device === 'oura' && <span style={{ color: data.color, fontSize: '7px', fontWeight: 'bold' }}>O</span>}
                      {device === 'neuphoria' && <span style={{ color: data.color, fontSize: '7px', fontWeight: 'bold' }}>N</span>}
                      {device === 'garmin' && <Activity size={8} style={{ color: data.color }} />}
                      {device === 'appleWatch' && <span style={{ color: data.color, fontSize: '7px', fontWeight: 'bold' }}>A</span>}
                    </div>
                  ))}
              </div>
              {/* Readiness score */}
              <span className={`text-xs font-semibold ${
                biometrics.compositeReadiness >= 85 ? 'text-emerald-400' :
                biometrics.compositeReadiness >= 70 ? 'text-green-400' :
                biometrics.compositeReadiness >= 55 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {biometrics.compositeReadiness}%
              </span>
            </div>
          )}
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 transition-transform group-hover:translate-x-1 text-[rgba(255,255,255,0.3)] group-hover:text-[#E8B800]" />
      </div>
    </button>
  );
}

/**
 * No team selected state
 */
function NoTeamSelected({ onSelectTeam }) {
  const isDarkMode = true; // OLED: always dark

  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
        'bg-white/10'
      }`}>
        <Users className={`w-10 h-10 ${'text-white/40'}`} />
      </div>
      <h2 className={`text-2xl font-bold mb-2 ${'text-white'}`}>
        No Team Selected
      </h2>
      <p className={`text-center max-w-md mb-8 ${'text-white/50'}`}>
        Select your team to view and manage your roster. We'll automatically sync player data from our database.
      </p>
      <button
        onClick={onSelectTeam}
        className="px-8 py-4 rounded-xl font-bold text-lg text-black bg-yellow-400 hover:bg-yellow-300 transition-colors flex items-center gap-2"
      >
        <Users className="w-5 h-5" />
        Select Your Team
      </button>
    </div>
  );
}

/**
 * Main component
 */
export default function RosterWithTeamSelection({ sportType = 'baseball' }) {
  const navigate = useNavigate();
  const isDarkMode = true; // OLED: always dark

  // Get selected team and roster from context/hook
  const {
    roster,
    loading,
    error,
    selectedTeam,
    teamName,
    leagueCode,
    sport,
    hasTeamSelected,
    refetch,
  } = useSelectedTeamRoster();

  const leagueName = selectedTeam?.league?.name || leagueCode;

  // Search state
  const [searchQuery, setSearchQuery] = React.useState('');

  // Filter roster by search
  const filteredRoster = useMemo(() => {
    if (!searchQuery) return roster;
    const query = searchQuery.toLowerCase();
    return roster.filter(player =>
      player.player_name?.toLowerCase().includes(query) ||
      player.position?.toLowerCase().includes(query) ||
      player.jersey_number?.toString().includes(query)
    );
  }, [roster, searchQuery]);

  // Handlers
  const handleSelectTeam = () => {
    navigate('/select-sport');
  };

  const handlePlayerClick = (player) => {
    // Navigate to player profile based on sport
    const prefix = sport === 'basketball' ? '/basketball' : '/baseball';
    navigate(`${prefix}/player/${player.player_id}`);
  };

  // No team selected
  if (!hasTeamSelected) {
    return (
      <div className={`min-h-full p-6 ${'bg-black'}`}>
        <NoTeamSelected onSelectTeam={handleSelectTeam} />
      </div>
    );
  }

  return (
    <div className={`min-h-full p-6 ${'bg-black'}`}>
      {/* Team Header */}
      <TeamHeader
        teamName={teamName}
        leagueName={leagueName}
        sport={sport}
        onChangeTeam={handleSelectTeam}
      />

      {/* Search Bar */}
      <div className="relative mb-6 max-w-md">
        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${
          'text-white/40'
        }`} />
        <input
          type="text"
          placeholder="Search players by name, position..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-colors ${
            'bg-white/5 border-white/10 text-white placeholder-white/40 focus:border-yellow-400/50'
          } focus:outline-none`}
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className={`w-10 h-10 animate-spin mb-4 ${
            'text-yellow-400'
          }`} />
          <p className={'text-white/50'}>
            Loading roster...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`p-6 rounded-xl text-center ${
          'bg-red-500/10 text-red-400'
        }`}>
          <p className="mb-4">{error.message}</p>
          <button
            onClick={refetch}
            className={`px-4 py-2 rounded-lg font-medium ${
              'bg-red-500/20 hover:bg-red-500/30'
            }`}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredRoster.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16">
          <Users className={`w-12 h-12 mb-4 ${'text-white/20'}`} />
          <p className={'text-white/50'}>
            {searchQuery
              ? 'No players match your search'
              : 'No players found for this team'
            }
          </p>
        </div>
      )}

      {/* Roster Grid */}
      {!loading && !error && filteredRoster.length > 0 && (
        <>
          <div className={`text-sm mb-4 ${'text-white/50'}`}>
            {filteredRoster.length} player{filteredRoster.length !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredRoster.map((player) => (
              <PlayerCard
                key={player.player_id}
                player={player}
                isDarkMode={isDarkMode}
                onClick={() => handlePlayerClick(player)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
